import * as React from 'react';
import classnames from 'classnames';
import { ICoreContext } from '@msdyn365-commerce/core';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { humanizeUnitOfMeasure } from '../unit-of-measure';

export interface IQuantityInputProps {
    mode?: 'edit' | 'view';
    context: ICoreContext;
    disable?: boolean;
    product: SimpleProduct | undefined;
    quantity: number;
    breakoff: boolean;
    min?: number;
    showOnlyIncrementDecrement?: boolean;
    showNonBreakoffAmount: boolean;
    nonBreakoffUOM?: string;
    onQuantityChange: (quantity: number) => void;
    BuyButton?: React.ReactNode;
    allowZero?: boolean;
}

function isDecimalAllowed(product: SimpleProduct | undefined, breakoff: boolean): boolean {
    // Only relevant for kg products
    if (!product || product.DefaultUnitOfMeasure !== 'kg') {
        return false;
    }

    const multipleOfQuantity = product.Behavior?.MultipleOfQuantity;

    // If the product has breakoff available only allow if breakoff is enabled
    if (multipleOfQuantity && multipleOfQuantity > 1) {
        return breakoff;
    }

    // Otherwise always allow decimal
    return true;
}

const QuantityInput = (props: IQuantityInputProps) => {
    const {
        mode = 'edit',
        context,
        disable,
        product,
        quantity,
        breakoff,
        min,
        // @ts-expect-error
        showOnlyIncrementDecrement,
        // @ts-expect-error
        showNonBreakoffAmount,
        nonBreakoffUOM,
        onQuantityChange,
        allowZero
    } = props;

    const [endsWithDecimal, setEndsWithDecimal] = React.useState(false);
    const [showEmpty, setShowEmpty] = React.useState(false);

    React.useEffect(() => {
        // Update quantity to 1, respecting the multiplier if applicable
        if (!product || allowZero || !allowZero && quantity > 1) {
            return;
        }
        const multiplier = breakoff ? 1 : product.Behavior?.MultipleOfQuantity || 1;
        onQuantityChange(validateQuantity(1 * multiplier));
    }, [product, quantity]);

    const baseClassName = 'quantity-input';
    // Check if there is a friendly UOM defined on the SimpleProduct
    const friendlyUOM = product?.ExtensionProperties?.find((x) => x.Key === 'FRIENDLYUOM')?.Value?.StringValue;
    // @ts-expect-error
    const breakoffUOM = humanizeUnitOfMeasure(context, friendlyUOM || product?.DefaultUnitOfMeasure, product);
    const uom = breakoff || !nonBreakoffUOM
        ? humanizeUnitOfMeasure(context, friendlyUOM || product?.DefaultUnitOfMeasure, product)
        : humanizeUnitOfMeasure(context, nonBreakoffUOM);
    const allowDecimal = isDecimalAllowed(product, breakoff);

    // const selectedUOM = breakoff ? uom : 'KASSE(R)';
    const multiplier = breakoff ? 1 : product?.Behavior?.MultipleOfQuantity || 1;
    const minimumQuantity = (min || +!allowZero) * multiplier;

    const validateQuantity = (quantity: number): number => {
        const newQuantity = Math.max(minimumQuantity, quantity);
        return Math.round(newQuantity * 100) / 100; // Round to two decimal places
    };

    const onIncrement = () => {
        const newQuantity = validateQuantity(quantity + multiplier);

        onQuantityChange(newQuantity);
    };

    const onDecrement = () => {
        const newQuantity = validateQuantity(Math.max(0, quantity - multiplier));
        onQuantityChange(newQuantity);
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Preserve the decimal character at the end if the user is typing a decimal number
        setEndsWithDecimal(e.target.value.endsWith('.'));
        setShowEmpty(e.target.value === '');

        // We're essentially changing the displayQuantity instead of the actual quantity
        const parsedValue = parseFloat(e.target.value) || 0;
        const newQuantity = breakoff
            ? validateQuantity(parsedValue)
            : validateQuantity(parsedValue * multiplier);

        onQuantityChange(newQuantity);
    };

    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    const displayQuantity = showEmpty ? '' : product && parseFloat((quantity / multiplier).toFixed(2)) + (endsWithDecimal ? '.' : '');
    const isM1ProductAndHasBreakoff = product?.DefaultUnitOfMeasure?.endsWith('-M1') && product.Behavior?.MultipleOfQuantity && product.Behavior.MultipleOfQuantity > 1;

    return (
        <div className={baseClassName}>
            <div className={`${baseClassName}__quantity-container`}>
                <div className={`${baseClassName}__quantity-controls`}>
                    {mode === 'edit' && (
                        <button
                            className={`${baseClassName}__quantity-controls__minus`}
                            disabled={disable || !product || quantity === minimumQuantity}
                            onClick={onDecrement}
                        >
                            -
                        </button>
                    )}
                    <input
                        className={classnames(`${baseClassName}__quantity-controls__input`, mode)}
                        readOnly={mode !== 'edit'}
                        disabled={disable}
                        min={min}
                        onKeyPress={(e) => {
                            if (e.key === '.' && allowDecimal) {
                                return;
                            }

                            if (!/[0-9]/.test(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        onChange={onInputChange}
                        onBlur={(e) => {
                            // If the input is empty, set it to 0
                            if (e.target.value === '') {
                                onInputChange({ target: { value: '0' } } as React.ChangeEvent<HTMLInputElement>);
                            }
                        }}
                        value={displayQuantity}
                        data-nomodalcss />
                    <span className={classnames(`${baseClassName}__quantity-controls__uom`, { disabled: disable })} onClick={(e) => {
                        // Set focus to input above
                        const input = (e.target as HTMLElement).previousElementSibling as HTMLInputElement;
                        if (input) {
                            input.focus();
                        }
                    }}>
                        {!!isM1ProductAndHasBreakoff && `x ${product!.Behavior!.MultipleOfQuantity} `}
                        {uom}
                    </span>
                    {mode === 'edit' && (
                        <button
                            className={`${baseClassName}__quantity-controls__plus`}
                            disabled={disable || !product}
                            onClick={onIncrement}
                        >
                            +
                        </button>
                    )}
                    {/* {!showOnlyIncrementDecrement && product && (
                            <span className={`${baseClassName}__quantity-controls__quantity`}>
                            {showNonBreakoffAmount && (!breakoff && multiplier > 1)
                                ? nonBreakoffUOM
                                    ? `${displayQuantity} ${uom}\n${quantity} ${breakoffUOM}`
                                    : `${displayQuantity} X ${multiplier} ${uom}`
                                : `${displayQuantity} ${uom}` }
                        </span>
                    )} */}
                </div>

                <div className={`${baseClassName}__buy-container`}>
                    {props.BuyButton}
                </div>
            </div>
        </div>
    );
};

export default QuantityInput;
